import { MODEL_NAMES } from '../../constants/index';
import { TrackedSet } from 'tracked-built-ins';
import { cached, tracked } from '@glimmer/tracking';
import {
  contactIdentityKey,
  selectedParticipantsProperties,
} from '../../utils/messaging/contact-identity';
import { didCancel, task } from 'ember-concurrency';
import {
  pageNumberInfinityModel,
  pageNumberInfinityOptions,
} from '../../utils/messaging/infinity-model';
import { service } from '@ember/service';
import { testAwareTimeout } from 'ember-simplepractice/utils/waiters';
import MessagingRoutesBaseHandler from './base-handler.js';

export const CONTACT_PAGE_SIZE = 15;
export const CONTACT_ROLE_FILTER = {
  role: ['primary clinician', 'couple', 'primary clinician for contact'],
};

/**
 * Abstract base handler for contact selection screens (Compose, Add Members).
 * @abstract
 */
export default class MessagingRoutesContactsHandler extends MessagingRoutesBaseHandler {
  @service('spds/toast') toast;
  @service infinity;
  @service intl;
  @service messaging;
  @service messagingLogging;
  @service messagingNavigation;

  constructor(owner, options) {
    super(owner, options);

    if (new.target === MessagingRoutesContactsHandler) {
      throw new Error(
        'MessagingRoutesContactsHandler is an abstract class and cannot be instantiated directly.'
      );
    }
  }

  @tracked search = '';
  @tracked contacts = [];

  selectedContactIds = new TrackedSet();
  lockedContactIds = new TrackedSet();

  /** Map of selection id → contact. Persists across search so we can resolve
  selected ids to contacts and reuse references. */
  #contactsCache = new Map();

  get conversation() {
    return this.options.conversation;
  }

  get hasSearchTerm() {
    return Boolean(this.search?.trim());
  }

  // Used to know when we shouldn't display "recent" and "related" client headers.
  get lastFetchWasSearch() {
    return Boolean(this.getContacts.lastComplete?.args?.[0]?.search);
  }

  @cached
  get selectableContacts() {
    const contacts = this.contacts;
    if (!contacts || !contacts.length) return [];

    return contacts.content.map(contact => this.#getOrCreateCachedContact(contact));
  }

  get selectedContacts() {
    return Array.from(this.selectedContactIds)
      .map(id => this.#contactsCache.get(id))
      .filter(Boolean);
  }

  @cached
  get selectedParticipantsProperties() {
    return selectedParticipantsProperties(this.selectedContacts);
  }

  get hasSelectedClient() {
    return Boolean(this.selectedParticipantsProperties.selected_client_external_ids.length);
  }

  get getOnlyRelatedClients() {
    if (!this.showListSections) return false;
    return this.hasSelectedClient && !this.hasSearchTerm;
  }

  get submitCount() {
    return this.selectedContacts.length;
  }

  get isSubmitDisabled() {
    return this.submitCount < 1;
  }

  get relatedEmptyStateKey() {
    return 'emc.contacts_page.no_related_contacts_found';
  }

  augmentFilter(filter) {
    return filter;
  }

  getLockedContacts(_currentUser) {
    return [];
  }

  rememberSelectedContact(contact, { locked = false } = {}) {
    const key = contactIdentityKey(contact);
    this.#contactsCache.set(key, contact);
    this.selectedContactIds.add(key);
    if (locked) this.lockedContactIds.add(key);
  }

  prepareSelection(currentUser) {
    for (const contact of this.getLockedContacts(currentUser)) {
      this.rememberSelectedContact(contact, { locked: true });
    }
  }

  submit(_selectedContacts) {}

  leave() {}

  onVisit = task({ drop: true }, async () => {
    this.prepareSelection(this.messaging.currentProfile?.user);
    await this.searchContacts.perform();
  });

  searchContacts = task({ restartable: true }, async () => {
    const searchValue = this.search?.trim();
    const filter = { ...(searchValue && { search: searchValue }) };

    // Add debounce only for search input (not initial load)
    if (this.search) {
      await testAwareTimeout(300);
    }

    try {
      await this.getContacts.perform(filter);
    } catch (err) {
      this.handleError(err);
    }
  });

  getContacts = task({ restartable: true }, async (filter = {}) => {
    filter = this.augmentFilter(filter, {
      relatedClientsOnly: this.getOnlyRelatedClients,
    });

    const model = await this.infinity.model(
      MODEL_NAMES.CONTACT,
      {
        ...(Object.keys(filter).length && { filter }),
        include: 'couple',
        ...this.selectedParticipantsProperties,
        ...pageNumberInfinityOptions(CONTACT_PAGE_SIZE),
      },
      pageNumberInfinityModel(CONTACT_PAGE_SIZE)
    );

    this.contacts = model;
  });

  updateSearch(search) {
    this.search = search;
    this.searchContacts.perform();
  }

  clearSearch() {
    this.updateSearch('');
  }

  loadMoreContacts = task({ drop: true }, async model => {
    if (!model.canLoadMore) return;
    try {
      await this.infinity.loadNextPage(model);
    } catch (err) {
      this.handleError(err);
    }
  });

  handleError(err) {
    if (didCancel(err)) return;
    this.messagingLogging.error(err, { context: 'contacts' });
    this.toast.error(this.intl.t('emc.general_error'));
  }

  selectContact = contact => {
    if (this.multiSelect) {
      this.toggleContactSelection(contact);
    } else {
      this.submitSelection.perform([contact]);
    }
  };

  toggleContactSelection = contact => {
    const key = contactIdentityKey(contact);
    if (this.lockedContactIds.has(key)) {
      return;
    }

    if (this.selectedContactIds.has(key)) {
      this.selectedContactIds.delete(key);
      this.searchContacts.perform();
    } else {
      this.selectedContactIds.add(key);
      this.clearSearch();
    }
  };

  get isSubmitting() {
    return this.submitSelection.isRunning;
  }

  submitSelection = task({ drop: true }, async contacts => {
    await this.submit(contacts);
  });

  openConversationWithContacts = async contacts => {
    try {
      const conversation = await this.messaging.findOrCreateConversationWithContacts(contacts);
      this.messagingNavigation.toConversation(conversation);
    } catch (err) {
      this.handleError(err);
    }
  };

  #getOrCreateCachedContact(contact) {
    this.#contactsCache.set(contactIdentityKey(contact), contact);
    return contact;
  }
}
